/* eslint-disable valid-typeof */

/**
 * Validation function for a Vue component's prop
 *
 * See <https://vuejs.org/v2/guide/components-props.html#Prop-Validation> for info on prop validation.
 * Since `propValidator` is a function, we just return the function inside a validator function:
 * `validator(value) { return return propValidator(value, {...}) }`
 *
 * Args:
 *      obj (Object): Usually just the pass-through of the `value` variable from the validator.
 *      rules (Object): Rules to check for.
 *
 * Example:
 *      export default {
 *          props: {
 *              textMessage: {
 *                  type: String,
 *
 *                  required: true,
 *
 *                  validator(value) {
 *                      return propValidator(value, {
 *                          checked: String,
 *                          unchecked: String,
 *                      });
 *                  },
 *              }
 *          }
 *      }
 *
 * Returns:
 *      (Boolean) True if prop is valid; false if invalid. Throws console error if invalid.
 */
export function propValidator(obj, rules) {
    return Object.entries(rules).every((rule) => {
        const [prop, conditions] = rule;

        if (typeof conditions === 'function') {
            const type = conditions.name.toLowerCase();

            switch (true) {
                case !!obj[prop] && type === 'array' && !Array.isArray(obj[prop]):
                    console.warn(`Validation failed. Expected "${ prop }" to be an array.`);
                    return false;

                case !!obj[prop] && type !== 'array' && typeof obj[prop] !== type:
                    console.warn(`Validation failed. Expected "${ prop }" to be a ${ type }.`);
                    return false;

                default:
                    return true;
            }
        }

        if (typeof conditions === 'object') {
            const { type: typeConstructor, required } = conditions;

            if (typeof typeConstructor !== 'function') return true;

            const type = typeConstructor.name.toLowerCase();

            switch (true) {
                case !!required && !obj[prop]:
                    console.warn(`Validation failed. The required property "${ prop }" is missing.`);
                    return false;

                case !!obj[prop] && typeof obj[prop] !== type:
                    console.warn(`Validation failed. Expected "${ prop }" to be a ${ type }.`);
                    return false;

                default:
                    return true;
            }
        }

        return true;
    });
}
